import React from "react";
// UI Framework
import { toast } from "react-toastify";
// Common components
import {
  FullScreenLoading,
  Form,
  TextInput,
  Button,
  StaticDropdown,
} from "src/Components";
import UsersDropdown from "src/Components/Dropdown/Users/UsersDropdown";
// requests
import {
  addFundNoRisk,
  editFundNoRisk,
  getBuyingPowerCriteria,
  getSandooghsDropDown,
} from "src/Requests/fundNoRiskTask";
// types
import {
  IFundNoRiskAddTaskDTO,
  IBuyingPowerCriterionDropdownDTO,
  ISandooghDropdownDTO,
} from "src/Interfaces/fundNoRisk";
// Hooks
import { useAppDispatch, useAppSelector } from "src/App/hooks";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
import { addComma, removeComma } from "src/helper";
// Reducers
import { setAddEditFundNotRiskFormValues } from "src/Features/FundNoRisk/fundNoRiskSlice";

interface Props {
  onSuccess: () => void;
  onClose: () => void;
}
const AddTask: React.FC<Props> = (props) => {
  // hooks
  const dispatch = useAppDispatch();

  const formValues = useAppSelector(
    (store) => store.fundNoRisk.addEditFormValues ?? {}
  );

  // local states
  const [loading, setLoading] = React.useState<boolean>(false);

  const [fundInstruments, setfundInstruments] = React.useState<
    ISandooghDropdownDTO[]
  >([]);
  const [buyingPowerCriteria, setBuyingPowerCriteria] = React.useState<
    IBuyingPowerCriterionDropdownDTO[]
  >([]);

  React.useEffect(() => {
    Promise.all([getSandooghsDropDown(), getBuyingPowerCriteria()])
      .then(([instruments, criteria]) => {
        setfundInstruments(instruments);
        setBuyingPowerCriteria(criteria);
      })
      .catch(packetErrorHandler);
  }, []);

  const changeHandler = (name: string, value: any) => {
    dispatch(
      setAddEditFundNotRiskFormValues({
        ...formValues,
        [name]: value,
      })
    );
  };

  const isPercent = formValues.thresholdType === 1;
  const thresholdsInvalid = [
    formValues.minBuyPower,
    formValues.buyPowerTarget,
  ].some(
    (value) =>
      !Number.isFinite(Number(value ?? 0)) ||
      (isPercent && (Number(value ?? 0) < 0 || Number(value ?? 0) > 100))
  );

  const changePriceHandler = (name: string, value: string) => {
    const normalizedValue = removeComma(value);
    if (isPercent) {
      if (!/^\d*\.?\d*$/.test(normalizedValue)) return;
      if (
        normalizedValue !== "" &&
        (!Number.isFinite(Number(normalizedValue)) || Number(normalizedValue) > 100)
      ) return;
    } else if (!/^-?\d*$/.test(normalizedValue)) return;

    dispatch(
      setAddEditFundNotRiskFormValues({
        ...formValues,
        [name]: normalizedValue,
      })
    );
  };
  const getProperFunction = () => {
    const data = {
      ...formValues,
      buyingPowerCriterion: formValues.buyingPowerCriterion ?? 0,
      thresholdType: formValues.thresholdType ?? 0,
      minBuyPower: Number(formValues.minBuyPower ?? 0),
      buyPowerTarget: Number(formValues.buyPowerTarget ?? 0),
    };

    if (data.id) return editFundNoRisk(data);
    return addFundNoRisk(data);
  };

  const clickHandler = () => {
    if (thresholdsInvalid) {
      toast.error(
        isPercent
          ? "کف و هدف باید بین ۰ تا ۱۰۰ درصد باشند"
          : "کف و هدف باید عدد معتبر باشند"
      );
      return;
    }
    setLoading(true);
    getProperFunction()
      .then((resp) => {
        if (!resp.data) toast.error(resp.msg);
        else {
          toast.success(resp.msg);
          props.onSuccess();
          props.onClose();
          dispatch(
            setAddEditFundNotRiskFormValues({} as IFundNoRiskAddTaskDTO)
          );
        }
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <Form className="!w-80 gap-3 p-2 px-2 !mx-auto">
      {loading ? <FullScreenLoading /> : <></>}
      <div className="text-[10px] whitespace-nowrap w-auto">نام اکانت</div>
      <UsersDropdown
        label="نام اکانت"
        name="accountId"
        value={formValues.accountId}
        onChange={changeHandler}
      />
      <StaticDropdown
        dropdown={{
          name: "buyingPowerCriterion",
          placeholder: "معیار انتخابی",
          extraLabel: true,
        }}
        onChange={(name, value) => changeHandler(name, Number(value))}
        options={buyingPowerCriteria}
        value={(formValues.buyingPowerCriterion ?? 0).toString()}
      />
      <StaticDropdown
        dropdown={{
          name: "thresholdType",
          placeholder: "نوع کف و هدف",
          extraLabel: true,
        }}
        onChange={(name, value) => {
          const nextType = Number(value);
          if (nextType === (formValues.thresholdType ?? 0)) return;
          dispatch(
            setAddEditFundNotRiskFormValues({
              ...formValues,
              thresholdType: nextType,
              minBuyPower: 0,
              buyPowerTarget: 0,
            })
          );
        }}
        options={[{ id: 0, name: "ارزش" }, { id: 1, name: "% از پرتفو" }]}
        value={(formValues.thresholdType ?? 0).toString()}
      />
      <TextInput
        label="حداقل معیار"
        name="minBuyPower"
        value={isPercent
          ? String(formValues.minBuyPower ?? "")
          : addComma(formValues.minBuyPower ?? 0)}
        endIcon={isPercent ? <span>%</span> : undefined}
        direction="ltr"
        onChange={changePriceHandler}
        extraLabel
      />
      <TextInput
        label="معیار هدف"
        name="buyPowerTarget"
        value={isPercent
          ? String(formValues.buyPowerTarget ?? "")
          : addComma(formValues.buyPowerTarget ?? 0)}
        endIcon={isPercent ? <span>%</span> : undefined}
        direction="ltr"
        onChange={changePriceHandler}
        extraLabel
      />
      <StaticDropdown
        dropdown={{
          name: "firstSellPriority",
          placeholder: "اولویت اول فروش",
          extraLabel: true,
        }}
        onChange={changeHandler}
        options={fundInstruments}
        value={formValues.firstSellPriority?.toString()}
      />
      <StaticDropdown
        dropdown={{
          name: "secondSellPriority",
          placeholder: "اولویت دوم فروش",
          extraLabel: true,
        }}
        onChange={changeHandler}
        options={fundInstruments}
        value={formValues.secondSellPriority?.toString()}
      />
      <StaticDropdown
        dropdown={{
          name: "buyInstrumentTarget",
          placeholder: "نماد هدف خرید",
          extraLabel: true,
        }}
        onChange={changeHandler}
        options={fundInstruments}
        value={formValues.buyInstrumentTarget?.toString()}
      />

      <Button
        text="ثبت"
        onClick={clickHandler}
        variant="contained"
        disabled={loading || thresholdsInvalid}
      />
    </Form>
  );
};

export default AddTask;
